import React, { useEffect, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import { getArticleBySlug } from "../blog";

const SORO_EMBED_SRC =
  "https://app.trysoro.com/api/embed/7e59f945-8a20-472e-975d-605fb7e8439c";

/* ------------------------------------------------------------------ *
 * FUTURE SORO FEED/API HOOK  (documented integration point)
 * The daily blog is published via the Soro embed (client-rendered). Soro does
 * not expose a discoverable content feed in this codebase, so its posts cannot
 * currently be turned into distinct server-rendered /blog/:slug pages from our
 * code alone. If/when a Soro feed or content API becomes available, set
 * REACT_APP_SORO_FEED_URL and implement the fetch below to generate real
 * article pages + sitemap entries per slug.
 * ------------------------------------------------------------------ */
export const SORO_FEED_URL = process.env.REACT_APP_SORO_FEED_URL || null;
export async function fetchSoroArticleBySlug(/* slug */) {
  if (!SORO_FEED_URL) return null; // no feed configured — safe no-op
  return null;
}

const titleFromSlug = (slug) =>
  slug
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export default function Blog() {
  const [params] = useSearchParams();
  const legacyPost = params.get("post");
  const localArticle = legacyPost ? getArticleBySlug(legacyPost) : null;
  const isQueryPost = !!legacyPost && !localArticle;

  // Real metadata scraped (read-only) from the Soro-rendered article, used to
  // enrich our JSON-LD and document title. Never invented.
  const [soroMeta, setSoroMeta] = useState({ heading: null, date: null, image: null });

  const postUrl = legacyPost
    ? `https://timefurniture.ae/blog?post=${encodeURIComponent(legacyPost)}`
    : "https://timefurniture.ae/blog";
  const articleHeadline = isQueryPost ? soroMeta.heading || titleFromSlug(legacyPost) : null;
  const articleDesc = isQueryPost
    ? `${soroMeta.heading || titleFromSlug(legacyPost)} — practical interior, fit-out and curtain insights from the Time Furniture team in Dubai and the UAE.`
    : null;

  // Build a single, safe BlogPosting for query posts (guaranteed present even
  // before/without Soro). datePublished + image only added when obtained.
  const articleJsonLd = isQueryPost
    ? (() => {
        const ld = {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: articleHeadline,
          description: articleDesc,
          url: postUrl,
          mainEntityOfPage: postUrl,
          author: { "@id": "https://timefurniture.ae/#organization" },
          publisher: { "@id": "https://timefurniture.ae/#organization" },
          isPartOf: { "@type": "Blog", url: "https://timefurniture.ae/blog" },
        };
        if (soroMeta.date) { ld.datePublished = soroMeta.date; ld.dateModified = soroMeta.date; }
        if (soroMeta.image) ld.image = soroMeta.image;
        return ld;
      })()
    : {
        "@context": "https://schema.org",
        "@type": "Blog",
        url: "https://timefurniture.ae/blog",
        name: "Time Furniture Blog",
        publisher: { "@id": "https://timefurniture.ae/#organization" },
      };

  usePageMeta({
    title: isQueryPost
      ? `${articleHeadline} | Time Furniture Journal`
      : "Blog | Curtains, Interiors & Fit-Out Insights — Time Furniture",
    description: isQueryPost
      ? articleDesc
      : "Articles on curtains, interior fit-out, upholstery and home automation in Dubai and Sharjah — practical insights from the Time Furniture team.",
    canonical: postUrl,
    ogType: isQueryPost ? "article" : "website",
    ogImage: isQueryPost && soroMeta.image ? soroMeta.image : undefined,
    jsonLd: articleJsonLd,
  });

  // Load the Soro embed script only while this page is mounted.
  useEffect(() => {
    const script = document.createElement("script");
    script.src = SORO_EMBED_SRC;
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      script.remove();
      const container = document.getElementById("soro-blog");
      if (container) container.replaceChildren();
    };
  }, []);

  // Read-only scrape of the Soro-rendered single post: heading (for title/JSON-LD),
  // published date (<time datetime>), and hero image. Never mutates the widget.
  // Also dedupes BlogPosting JSON-LD so only ONE remains after Soro loads
  // (keeping our synchronous, controlled node with data-seo="page").
  useEffect(() => {
    if (!isQueryPost) return undefined;
    const root = document.getElementById("soro-blog");
    if (!root) return undefined;

    const dedupeBlogPosting = () => {
      const nodes = Array.from(document.querySelectorAll('script[type="application/ld+json"]'));
      const bp = nodes.filter((n) => {
        try { return JSON.parse(n.textContent)["@type"] === "BlogPosting"; } catch { return false; }
      });
      if (bp.length <= 1) return;
      const keep = bp.find((n) => n.getAttribute("data-seo") === "page") || bp[0];
      bp.forEach((n) => { if (n !== keep) n.remove(); });
    };

    const read = () => {
      const h = root.querySelector("h1, h2");
      const heading = h && h.textContent ? h.textContent.trim() : null;
      const timeEl = root.querySelector("time[datetime]");
      const date = timeEl ? timeEl.getAttribute("datetime") : null;
      const imgEl = root.querySelector("img[src]");
      let image = imgEl ? imgEl.getAttribute("src") : null;
      if (image && image.startsWith("/")) image = `https://timefurniture.ae${image}`;
      setSoroMeta((prev) => {
        const next = { heading: heading || prev.heading, date: date || prev.date, image: image || prev.image };
        if (next.heading && next.heading.length > 3) {
          document.title = `${next.heading} | Time Furniture Journal`;
          const og = document.head.querySelector('meta[property="og:title"]');
          if (og) og.setAttribute("content", next.heading);
        }
        return next;
      });
      dedupeBlogPosting();
    };
    const obs = new MutationObserver(read);
    obs.observe(root, { childList: true, subtree: true });
    read();
    // Belt-and-suspenders: dedupe again after Soro's async schema injection.
    const t1 = setTimeout(dedupeBlogPosting, 1500);
    const t2 = setTimeout(dedupeBlogPosting, 3500);
    return () => { obs.disconnect(); clearTimeout(t1); clearTimeout(t2); };
  }, [isQueryPost]);

  // Legacy clean-URL redirect: /blog?post=<slug> → /blog/<slug> for curated
  // (locally-defined) articles, without losing the post.
  if (localArticle) {
    return <Navigate to={`/blog/${localArticle.slug}`} replace />;
  }

  return (
    <div data-testid="blog-page" className="bg-[#FAF5EC]">
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Blog" }]} />

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-12">
        {isQueryPost ? (
          // Embedded article present → shell title must NOT be an <h1>. Soro
          // renders the single article <h1>, guaranteeing exactly one H1.
          <p data-testid="blog-shell-eyebrow" className="eyebrow">
            Journal · Article
          </p>
        ) : (
          <>
            <p className="eyebrow mb-6">Journal</p>
            <h1
              data-testid="blog-h1"
              className="font-serif-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-[#1C110C] max-w-5xl"
            >
              Notes from the{" "}
              <em className="text-[#6E1F2C] not-italic">atelier</em>.
            </h1>
            <p className="mt-8 max-w-2xl text-[#3E2A22] leading-relaxed text-lg">
              Practical insights on curtains, fit-out and home automation —
              written by our team for clients planning their next project.
            </p>
          </>
        )}
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-28 md:pb-40">
        <div id="soro-blog" data-testid="soro-blog-widget" />
      </section>
    </div>
  );
}
